class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (cartItems) cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

if (!customElements.get('cart-remove-button')) customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
    });
    this.syncVisibleQuantityButtons();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    const input = event.target;
    if (!input || !input.matches('.quantity__input')) return;

    let quantity = Number.parseInt(input.value || '0', 10);
    if (!Number.isFinite(quantity)) quantity = Number.parseInt(input.min || '1', 10) || 1;

    // quantity=0 is Shopify's canonical remove-line operation. Never clamp it back to min.
    if (quantity !== 0) {
      const min = Number.parseInt(input.min || '1', 10) || 1;
      const max = input.hasAttribute('max') ? Number.parseInt(input.max || '0', 10) : null;
      quantity = Math.max(min, quantity);
      if (Number.isFinite(max)) quantity = Math.min(max, quantity);
    }

    input.value = String(quantity);
    this.updateQuantity(
      input.dataset.index,
      quantity,
      document.activeElement?.getAttribute?.('name') || ''
    );
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=main-cart-items`)
      .then((response) => {
        if (!response.ok) throw new Error(`Cart section refresh failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const sourceQty = html.querySelector('cart-items');
        if (sourceQty && this.tagName === 'CART-ITEMS') {
          this.innerHTML = sourceQty.innerHTML;
          this.className = sourceQty.className;
          this.syncVisibleQuantityButtons();
        }
        return;
      })
      .catch((error) => console.error('Fraquane cart refresh:', error));
  }

  getSectionsToRender() {
    const sections = [];
    const mainCartItems = document.getElementById('main-cart-items');
    if (mainCartItems?.dataset?.id) {
      sections.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
    }

    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
    sections.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });

    const mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartFooter?.dataset?.id) {
      sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
    }
    return sections;
  }

  updateQuantity(line, quantity, name) {
    quantity = Number.parseInt(quantity || '0', 10);
    if (!Number.isFinite(quantity)) quantity = 0;

    const quantityInput =
      document.getElementById(`Quantity-${line}`) ||
      document.getElementById(`Drawer-quantity-${line}`);

    if (quantityInput && quantity !== 0) {
      const min = Number.parseInt(quantityInput.min || '1', 10) || 1;
      const max = quantityInput.hasAttribute('max') ? Number.parseInt(quantityInput.max || '0', 10) : null;
      quantity = Math.max(min, quantity);
      if (Number.isFinite(max)) quantity = Math.min(max, quantity);
      quantityInput.value = String(quantity);
    }

    this.enableLoading(line);
    const sectionsToRender = this.getSectionsToRender();
    const body = JSON.stringify({
      line,
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then(async (response) => {
        const state = await response.text();
        if (!response.ok) throw new Error(state || `Cart update failed (${response.status})`);
        return state;
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const currentQuantity =
          document.getElementById(`Quantity-${line}`) ||
          document.getElementById(`Drawer-quantity-${line}`);

        if (parsedState.errors) {
          if (currentQuantity) currentQuantity.value = currentQuantity.getAttribute('value') || currentQuantity.value;
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const isEmpty = parsedState.item_count === 0;
        this.classList.toggle('is-empty', isEmpty);
        document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

        sectionsToRender.forEach((section) => {
          const root = document.getElementById(section.id);
          const html = parsedState.sections?.[section.section];
          if (!root || !html) return;
          const target = root.querySelector(section.selector) || root;
          const next = this.getSectionInnerHTML(html, section.selector);
          if (next !== null) target.innerHTML = next;
        });

        this.updateLiveRegions(line);
        this.syncVisibleQuantityButtons();

        document.dispatchEvent(
          new CustomEvent('fraquane:cart:updated', { detail: parsedState })
        );

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items' });
      })
      .catch((error) => {
        console.error('Fraquane cart update:', error);
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => this.disableLoading(line));
  }


  syncVisibleQuantityButtons() {
    this.querySelectorAll('quantity-input').forEach((wrapper) => {
      const input = wrapper.querySelector('.quantity__input');
      if (!input) return;
      const value = Number.parseInt(input.value || '0', 10) || 0;
      const min = Number.parseInt(input.min || '1', 10) || 1;
      const max = input.hasAttribute('max') ? Number.parseInt(input.max || '0', 10) : null;
      const minus = wrapper.querySelector('button[name="minus"]');
      const plus = wrapper.querySelector('button[name="plus"]');
      if (minus) {
        minus.disabled = value <= min;
        minus.classList.toggle('disabled', minus.disabled);
      }
      if (plus) {
        plus.disabled = Number.isFinite(max) ? value >= max : false;
        plus.classList.toggle('disabled', plus.disabled);
        plus.setAttribute('aria-disabled', plus.disabled ? 'true' : 'false');
        plus.title = plus.disabled ? 'Quantité maximale disponible atteinte' : '';
      }
    });
  }

  updateLiveRegions(line, message = '') {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    const text = lineItemError?.querySelector('.cart-item__error-text');
    if (text) text.innerHTML = message;

    this.lineItemStatusElement?.setAttribute('aria-hidden', true);
    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (!cartStatus) return;
    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');
    [...this.querySelectorAll(`#CartItem-${line} .loading-overlay`), ...this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`)]
      .forEach((overlay) => overlay.classList.remove('hidden'));
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur();
    this.lineItemStatusElement?.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay, #CartDrawer-Item-${line} .loading-overlay`)
      .forEach((overlay) => overlay.classList.add('hidden'));
  }
}

if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}